/**
 * ShareStage — stage 5 of the decision page: the public record link, print,
 * and archiving. "Put it on the plan" lives with the decision itself
 * (DecisionPanel), because it is part of deciding, not of sharing.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, ClipboardDocumentIcon, EyeIcon } from '@heroicons/react/24/outline';
import { advanceBriefStatus } from '../../../services/brief/briefService';
import type { Brief } from '../../../types/brief';
import { btnSecondary } from '@/components/mono/primitives';

export function ShareStage({ brief, onBriefChange, onError }: { brief: Brief; onBriefChange: (b: Brief) => void; onError: (m: string) => void }) {
  const url = `${window.location.origin}/brief/view/${brief.view_token}`;
  const [copied, setCopied] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const decided = brief.status === 'decided' || brief.status === 'deferred';
  const archived = brief.status === 'archived';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      onError(`Could not copy. The link is ${url}`);
    }
  };

  const archive = async () => {
    setArchiving(true);
    try {
      onBriefChange(await advanceBriefStatus(brief.id, 'archived'));
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not archive.');
    } finally {
      setArchiving(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-[var(--text-sec)]">
        {brief.status === 'draft'
          ? 'The record link works once this decision is open. Anyone with it can read the record; nobody needs an account.'
          : 'Anyone with this link can read the record — the options, the proposals, and once decided, why. Nobody needs an account.'}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={copy} disabled={brief.status === 'draft'} {...btnSecondary()}>
          {copied ? <CheckIcon className="w-4 h-4 text-[var(--green)]" /> : <ClipboardDocumentIcon className="w-4 h-4" />}
          {copied ? 'Copied' : 'Copy the record link'}
        </button>
        {brief.status !== 'draft' && (
          <Link to={`/brief/view/${brief.view_token}`} target="_blank" {...btnSecondary()}>
            <EyeIcon className="w-4 h-4" /> Open the record
          </Link>
        )}
      </div>
      {decided && (
        <div className="pt-4 border-t border-[var(--rule)]">
          <p className="text-sm text-[var(--text-sec)] mb-2">Done with this one? Archiving keeps the record readable and moves it out of the way.</p>
          <button type="button" onClick={archive} disabled={archiving} {...btnSecondary()}>
            {archiving ? 'Archiving…' : 'Archive this decision'}
          </button>
        </div>
      )}
      {archived && <p className="text-sm text-[var(--text-dim)]">Archived. The record link still works.</p>}
    </div>
  );
}
